import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Flex,
  Grid,
  IconButton,
  Text,
  useBreakpointValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import Layout from "../../../layout";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAirportPickUpRequest,
  fetchGenerateTemplateRequest,
  fetchPatientDetailsRequest,
  fetchProvideOpinionRequest,
  fetchTemplateDataFieldsRequest,
  fetchVilTemplateRequest,
  fetchVilUpdateRequest,
  resetDoctorReminder,
  fetchDoctorReminderRequest,
} from "../ipsStore/actions";
import PrimaryButton from "../../../components/PrimaryButton"; // Assuming your PrimaryButton component is here
import PrimaryFormLabel from "../../../components/PrimaryFormLabel";
import SelectionModal from "./RecommendedSelectionModal";
import { useLocation, useNavigate } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import PassportModal from "./PassportModal";
import ReportsViewModal from "./ReportsViewModal";
import PrimarySideHeader from "../../../components/PrimarySideHeader";
import FlightTicketDetailsModal from "./FlightTicketDetailsModal";
import {
  downloadFilesRequest,
  updateTrDetailsRequest,
} from "../../common/store/actions";
import Alert from "../../../components/Alert";
import { FcCancel } from "react-icons/fc";
import RejectOpinion from "./RejectOpinionModal";
import AirportPickUpModal from "./AirportPickUpModal";
import AirportPickUpIcon from "../../../assets/images/PatientArrivalConfirmPickup.svg";
import RequestDetailsIcon from "../../../assets/images/PatientArrivalReqDetails.svg";
import ShareWithDoctorIcon from "../../../assets/images/ShareWithDoctor.svg";
import ShareWithOpinionIcon from "../../../assets/images/ShareOpinion.svg";
import RequestReportsIcon from "../../../assets/images/RequestReports.svg";
import ShareOpinionNonActive from "../../../assets/images/MOShareOpinionNonActive.png";
import ShareWithDoctorNonActive from "../../../assets/images/MOShareWithDoctorNonActive.png";
import RequestReportsNonActive from "../../../assets/images/MOReqReportsNonActive.png";
import CreateVILActiveIcon from "../../../assets/images/CreateVIL.svg";
import VilShareWithHCFIcon from "../../../assets/images/VilShareWithHCF.svg";
import CreateVILNonActiveIcon from "../../../assets/images/CreateVILNonActive.svg";
import VilShareWithHcfNonActiveIcon from "../../../assets/images/VilShareWithHcfNonActive.svg";
import RequestDetailsNonActiveIcon from "../../../assets/images/RequestDetailsNonActive.svg";
import AirportPortPickupNonActiveIcon from "../../../assets/images/PatientArrivalConfirmPickupNonActive.svg";
import ShareWithDoctorModal from "./ShareWithDoctorModal";
import MedicalOpinion from "./MedicalOpinion";
import { fetchDoctorProvidedOpinionRequest } from "../ipsStore/actions";
import ShareOpinionModal from "./ShareOpinionModal";
import TemplateForm from "./TemplateForm";
import PrimaryText from "../../../components/PrimaryText";
import GeneratedFileView from "./GeneratedFileView";
import { BsBellFill } from "react-icons/bs";
import VilTemplateSelectionView from "./VilTemplateSelectionView";
import RequestDetails from "./RequestDetails";
import ShareWithHCF from "./ShareWithHCF";

const ICON_CONFIGS = {
  patientArrivals: [
    [AirportPickUpIcon, "Airport Pickup", "handleAirportPickupIconClick"],
    [RequestDetailsIcon, "Request Details", "handleRequestDetails"],
  ],
  medicalOpinion: [
    [ShareWithOpinionIcon, "Share Opinion", "onOpenShareOpinionModal"],
    [ShareWithDoctorIcon, "Share with Doctor", "onOpenShareWithDoctorModal"],
    [RequestReportsIcon, "Request Reports", "handleReportsView"],
  ],
  vilRequest: [
    // [CreateVILActiveIcon, "Create VIL", "handleVilRequest"],
    [CreateVILActiveIcon, "View Details", "handleVilRequest2"],
    [RequestDetailsIcon, "Request Details", "handleRequestDetails"],
    [VilShareWithHCFIcon, "Share With HCF", "handleShareWithHCF"],
  ],
};

const IconGroup = ({ icons, handlers, patientDetails }) => {
  const getIconConfig = (icon, alt, handler) => {
    const status = patientDetails?.[0]?.ips_opinion_status;
    const vilSubStatus = patientDetails?.[0]?.visa_sub_status;

    // For medical opinion section
    if (
      alt === "Share Opinion" &&
      ["Need Additional Info", "Opinion Rejected", "Opinion Provided"].includes(
        status
      )
    ) {
      return [ShareOpinionNonActive, alt, null];
    }

    if (alt === "Share with Doctor") {
      if (status === "Pending From Doctor") {
        return [ShareWithDoctorNonActive, alt, null];
      }
      if (
        [
          "Need Additional Info",
          "Opinion Rejected",
          "Opinion Provided",
        ].includes(status)
      ) {
        return [ShareWithDoctorNonActive, alt, null];
      }
    }

    if (
      alt === "Request Reports" &&
      ["Need Additional Info", "Opinion Rejected", "Opinion Provided"].includes(
        status
      )
    ) {
      return [RequestReportsNonActive, alt, null];
    }
    // Flight Ticket section
    if (
      (alt === "Airport Pickup" && !patientDetails[0]?.is_airport_pickup) ||
      patientDetails[0]?.pickup_details.length < 0
    ) {
      return [AirportPortPickupNonActiveIcon, alt, null];
    }
    // For VIL section
    if (alt === "Create VIL" && vilSubStatus !== "Pending VIL") {
      return [CreateVILNonActiveIcon, alt, null];
    }

    if (alt === "Request Details" && vilSubStatus !== "Pending VIL") {
      return [RequestDetailsNonActiveIcon, alt, null];
    }

    // if (alt === "Share With HCF" && vilSubStatus !== "Sent To HCF") {
    //   return [VilShareWithHcfNonActiveIcon, alt, null];
    // }

    return [icon, alt, handler];
  };
  return (
    <Flex justify="center" gap={4} wrap="wrap" mt={4} px={2}>
      {icons.map(([icon, alt, handler]) => {
        const [finalIcon, finalAlt, finalHandler] = getIconConfig(
          icon,
          alt,
          handler
        );
        return (
          <Box key={finalAlt} textAlign="center">
            <Box
              as="img"
              src={finalIcon}
              alt={finalAlt}
              w={{ base: "100px", md: "150px" }}
              h="auto"
              cursor={finalHandler ? "pointer" : "default"}
              onClick={finalHandler && handlers[finalHandler]}
            />
            <PrimaryText>{finalAlt}</PrimaryText>
          </Box>
        );
      })}
    </Flex>
  );
};

const PatientDetails = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const trid = location.state?.trid;
  const isFromPatientArrivals = location.state?.from === "patient_arrivals";
  const isFromMedicalOpinion = location.state?.from === "medical_opinion";
  const isFromVilRequests = location.state?.from === "vil_requests";

  const {
    patientDetails,
    providedOpinion,
    isProvidedOpinion,
    airportPickUpDetails,
    templateDataFields,
    doctorProvidedOpinion,
    generateTemplateData,
    vilTemplates,
    vilUpdateResponse,
    doctorReminderResponse,
  } = useSelector((state) => state.ips);
  const { updateTrDetails, isTrDetailsNotUpdated } = useSelector(
    (state) => state.common
  );
  const isAttendantPassportsEmpty =
    !patientDetails[0]?.attendant_passports ||
    patientDetails[0].attendant_passports.length === 0;
  const isPatientPassportEmpty = patientDetails[0]?.patient_passport === null;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [isPatientPassport, setIsPatientPassport] = useState("");
  const [reports, setReports] = useState([]);
  const [rejectionOpinion, setRejectionOpinion] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState("Success!");
  const [alertType, setAlertType] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const [showMedicalOpinion, setShowMedicalOpinion] = useState(false);
  const [rejectedOpinionData, setRejectedOpinionData] = useState(null);
  const [selectedMType, setSelectedMType] = useState("");
  const [medicalOpinionRejectedButton, setMedicalOpinionRejectedButton] =
    useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showGeneratedFiles, setShowGeneratedFiles] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selectedVilTemplate, setSelectedVilTemplate] = useState(null);
  const [doctorReminder, setDoctorReminder] = useState(null);

  const {
    isOpen: isPassportModalOpen,
    onOpen: onOpenPassportModal,
    onClose: onClosePassportModal,
  } = useDisclosure();

  const {
    isOpen: isRejectModalOpen,
    onOpen: onOpenRejectModal,
    onClose: onCloseRejectModal,
  } = useDisclosure();

  const {
    isOpen: isAirportPickupModalOpen,
    onOpen: onOpenAirportPickupModal,
    onClose: onCloseAirportPickupModal,
  } = useDisclosure();

  const {
    isOpen: isFlightTicketModalOpen,
    onOpen: onOpenFlightTicketModal,
    onClose: onCloseFlightTicketModal,
  } = useDisclosure();

  const {
    isOpen: isShareWithDoctorModalOpen,
    onOpen: onOpenShareWithDoctorModal,
    onClose: onCloseShareWithDoctorModal,
  } = useDisclosure();

  const {
    isOpen: isShareOpinionModalOpen,
    onOpen: onOpenShareOpinionModal,
    onClose: onCloseShareOpinionModal,
  } = useDisclosure();

  const {
    isOpen: isVilProcessModalOpen,
    onOpen: onOpenVilProcessModal,
    onClose: onCloseVilProcessModal,
  } = useDisclosure();

  const {
    isOpen: isRequestDetailsModalOpen,
    onOpen: onOpenRequestDetailsModal,
    onClose: onCloseRequestDetailsModal,
  } = useDisclosure();

  const {
    isOpen: isShareWithHCFModalOpen,
    onOpen: onOpenShareWithHCFModal,
    onClose: onCloseShareWithHCFModal,
  } = useDisclosure();

  const {
    isOpen: isVilReportModalOpen,
    onOpen: onOpenVilReportModal,
    onClose: onCloseVilReportModal,
  } = useDisclosure();

  useEffect(() => {
    handleLoadPage();
  }, [trid, dispatch]);

  useEffect(() => {
    if (doctorReminderResponse) {
      setDoctorReminder(doctorReminderResponse);
    }
  }, [doctorReminderResponse]);

  useEffect(() => {
    if (doctorReminder) {
      setShowAlert(true);
      setAlertTitle("Successful!");
      setAlertType("success");
      setAlertMessage(doctorReminder?.message);
    }
  }, [doctorReminder]);

  useEffect(() => {
    if (isTrDetailsNotUpdated) {
      setShowAlert(true);
      setAlertTitle("Error!");
      setAlertType("error");
      setAlertMessage("Error occurred while updating the details.");
    } else {
      setShowAlert(false);
    }
  }, [isTrDetailsNotUpdated]);

  useEffect(() => {
    if (isProvidedOpinion) {
      if (providedOpinion.opinion_type === "rejected") {
        setShowAlert(true);
        setAlertTitle("Rejected!");
        setAlertType("error");
        setAlertMessage(providedOpinion.message);
      } else if (providedOpinion.opinion_type === "add_reports_requested") {
        setShowAlert(true);
        setAlertTitle("Successful!");
        setAlertType("success");
        setAlertMessage("Additional details have been requested successfully.");
      } else if (providedOpinion.opinion_type === "share_op_with_doctor") {
        setShowAlert(true);
        setAlertTitle("Successful!");
        setAlertType("success");
        setAlertMessage(providedOpinion.message);
      } else if (providedOpinion.opinion_type === "confirm_opinion") {
        setShowAlert(true);
        setAlertTitle("Successful!");
        setAlertType("success");
        setAlertMessage(providedOpinion.message);
        handleGeneratedFilesClose();
      } else {
        setShowAlert(false);
        handleLoadPage();
      }
    }
  }, [isProvidedOpinion, providedOpinion]);

  useEffect(() => {
    if (airportPickUpDetails?.action === "reject") {
      setShowAlert(true);
      setAlertTitle("Rejected!");
      setAlertType("error");
      setAlertMessage(airportPickUpDetails?.message);
    } else if (airportPickUpDetails?.action !== "reject") {
      setShowAlert(true);
      setAlertTitle("Successful!");
      setAlertType("success");
      setAlertMessage(airportPickUpDetails?.message);
    } else {
      setShowAlert(false);
    }
  }, [airportPickUpDetails]);

  useEffect(() => {
    if (vilUpdateResponse?.action === "rejected") {
      setShowAlert(true);
      setAlertTitle("Rejected!");
      setAlertType("error");
      setAlertMessage(vilUpdateResponse?.message);
    } else if (vilUpdateResponse?.action !== "rejected") {
      setShowAlert(true);
      setAlertTitle("Successful!");
      setAlertType("success");
      setAlertMessage(vilUpdateResponse?.message);
      setShowGeneratedFiles(false);
    } else {
      setShowAlert(false);
    }
  }, [vilUpdateResponse]);

  useEffect(() => {
    if (updateTrDetails) {
      setShowAlert(true);
      setAlertMessage("Details Updated Successfully");
    } else {
      setShowAlert(false);
    }
  }, [updateTrDetails]);

  useEffect(() => {
    if (
      doctorProvidedOpinion?.opinion_type === "Opinion Rejected" &&
      doctorProvidedOpinion?.opinion_rejected?.length > 0
    ) {
      setRejectedOpinionData(doctorProvidedOpinion.opinion_rejected[0]);
    }
  }, [doctorProvidedOpinion]);

  useEffect(() => {
    if (generateTemplateData && isGeneratingTemplate) {
      setShowGeneratedFiles(true);
    }
  }, [generateTemplateData, isGeneratingTemplate]);

  const handleLoadPage = () => {
    if (trid) {
      const payload = { tr_id: trid, source: "Web" };
      dispatch(fetchPatientDetailsRequest(payload));
    }
    dispatch(resetDoctorReminder());
  };

  const handleDoctorsEditClick = () => {
    setModalType("doctor");
    setIsModalOpen(true);
  };

  const handleEmbassyEditClick = () => {
    setModalType("embassy");
    setIsModalOpen(true);
  };

  const handleRemindClick = (trId) => {
    dispatch(
      fetchDoctorReminderRequest({
        tr_id: trId,
        source: "Web",
      })
    );
  };

  const handleModalSubmit = (selectedValue) => {
    let payload = {
      tr_id: trid,
      source: "Web",
    };

    if (modalType === "doctor") {
      payload.recommended_doctor = selectedValue;
    } else {
      payload.embassy = selectedValue;
    }

    dispatch(updateTrDetailsRequest(payload));
  };

  const handlePassportModal = (is_patient_passport) => {
    setIsPatientPassport(is_patient_passport);
    onOpenPassportModal();
  };

  const handleReportsView = () => {
    setReports(patientDetails[0]?.patient_reports);
    setIsReportsModalOpen(true);
  };

  const handleReportsModalSubmit = (requestedReports) => {
    let payload = {
      tr_id: trid,
      opinion_type: "add_reports_requested",
      opinion: requestedReports,
      source: "Web",
    };
    dispatch(fetchProvideOpinionRequest(payload));
  };

  const handleAirportPickUpSubmit = (driverDetails) => {
    const payload = new FormData();
    payload.append("tr_id", patientDetails[0]?.id);
    payload.append("action", "update_pickup_details");

    // Check if pickup details were previously provided
    const isPickupDetailsProvided =
      patientDetails[0]?.pickup_details_status === "Pickup Details Provided";

    if (isPickupDetailsProvided) {
      // Case 2: Pickup details already exist - only send changed fields
      const existingDetails = patientDetails[0].pickup_details[0];
      let hasUpdates = false;

      if (driverDetails.driverName !== existingDetails.driver_name) {
        payload.append("driver_name", driverDetails.driverName);
        hasUpdates = true;
      }
      if (driverDetails.vehicleNumber !== existingDetails.vehicle_no) {
        payload.append("vehicle_no", driverDetails.vehicleNumber);
        hasUpdates = true;
      }
      if (driverDetails.mobileNumber !== existingDetails.mobile_no) {
        payload.append("mobile_no", driverDetails.mobileNumber);
        hasUpdates = true;
      }
      if (driverDetails.pickupDate !== existingDetails.pickup_date) {
        payload.append("pickup_date", driverDetails.pickupDate);
        hasUpdates = true;
      }
      if (driverDetails.pickupTime !== existingDetails.pickup_time) {
        payload.append("pickup_time", driverDetails.pickupTime);
        hasUpdates = true;
      }
      if (
        driverDetails.vehicleTypeColor !== existingDetails.vehicle_type_color
      ) {
        payload.append("vehicle_type_color", driverDetails.vehicleTypeColor);
        hasUpdates = true;
      }

      if (hasUpdates) {
        payload.append("pickup_id", existingDetails.pickup_id);
        payload.append("source", "Web");
      }
    } else {
      // Case 1: New pickup details - send all fields
      payload.append("driver_name", driverDetails.driverName);
      payload.append("vehicle_no", driverDetails.vehicleNumber);
      payload.append("mobile_no", driverDetails.mobileNumber);
      payload.append("pickup_date", driverDetails.pickupDate);
      payload.append("pickup_time", driverDetails.pickupTime);
      payload.append("vehicle_type_color", driverDetails.vehicleTypeColor);
      payload.append("source", "Web");
    }

    dispatch(fetchAirportPickUpRequest(payload));
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleGoBack = () => {
    // Close all modals
    onClosePassportModal();
    onCloseRejectModal();
    onCloseAirportPickupModal();
    onCloseFlightTicketModal();
    onCloseShareWithDoctorModal();
    setIsModalOpen(false);
    setIsReportsModalOpen(false);
    setShowMedicalOpinion(false);
    setShowGeneratedFiles(false);
    setShowTemplateForm(false);
    setSelectedMType("");
    setInitialFormData(null);
    setSelectedVilTemplate(null);
    setIsGeneratingTemplate(false);
    onCloseShareWithHCFModal();
    navigate(-1);
  };

  const handleFlightTicketView = () => {
    onOpenFlightTicketModal();
  };

  const handleVilTemplateSelection = (selectedTemplate) => {
    const payload = {
      tr_id: trid,
      temp_id: selectedTemplate.id,
      template: "VIL",
      source: "Web",
    };
    setSelectedVilTemplate(selectedTemplate.id);
    dispatch(fetchTemplateDataFieldsRequest(payload));
    onCloseVilProcessModal();
    setShowTemplateForm(true);
    setSelectedMType("VIL");
  };

  const downloadFiles = (fileType) => {
    let payload = {
      tr_id: trid,
      file_type: fileType,
      source: "Web",
    };
    dispatch(downloadFilesRequest(payload));
  };

  const handleRejectPatientSubmit = (rejectionReason) => {
    let payload;

    if (isFromVilRequests) {
      payload = {
        tr_id: trid,
        action: "rejected",
        remarks: rejectionReason,
      };
      dispatch(fetchVilUpdateRequest(payload));
    } else if (isFromPatientArrivals) {
      payload = {
        tr_id: trid,
        action: "reject",
        remarks: rejectionReason,
      };
      dispatch(fetchAirportPickUpRequest(payload));
    } else {
      // Default case for medical opinion
      payload = {
        tr_id: trid,
        opinion_type: "rejected",
        opinion: rejectionReason,
        source: "Web",
      };
      dispatch(fetchProvideOpinionRequest(payload));
    }

    setRejectionOpinion("");
    setMedicalOpinionRejectedButton(false);
    onCloseRejectModal();
  };

  const handleAirportPickupButtonClick = () => {
    setIsViewOnlyMode(true);
    onOpenAirportPickupModal();
  };

  const handleAirportPickupIconClick = () => {
    setIsViewOnlyMode(false);
    onOpenAirportPickupModal();
  };

  const handleShareOpinionClick = (selectedOption) => {
    let payload = {
      tr_id: trid,
      source: "Web",
      template: selectedOption,
    };
    setSelectedMType(selectedOption);
    dispatch(fetchTemplateDataFieldsRequest(payload));
    onCloseShareOpinionModal();
    setShowTemplateForm(true);
  };

  const handleTemplateBack = () => {
    setShowTemplateForm(false);
  };

  const handleTemplateSubmit = (formData) => {
    const payload = {
      tr_id: trid,
      regenerate_font: false,
      source: "Web",
      generate_pdf: selectedMType,
      ...(selectedMType === "VIL" && {
        template_id: selectedVilTemplate,
      }),
      ...formData,
    };
    setInitialFormData(formData);
    setIsGeneratingTemplate(true);
    dispatch(fetchGenerateTemplateRequest(payload));
    setShowTemplateForm(false);
  };
  const handleShareWithDoctorSubmit = (selectedOptions) => {
    const payload = {
      tr_id: trid,
      opinion_type: "share_op_with_doctor",
      notification_type: selectedOptions.notificationType,
      source: "Web",
    };

    dispatch(fetchProvideOpinionRequest(payload));
  };

  const handleMedicalOpinionView = () => {
    if (patientDetails[0]?.ips_opinion_status === "Received From Doctor") {
      dispatch(
        fetchDoctorProvidedOpinionRequest({ tr_id: trid, source: "Web" })
      );
      if (patientDetails[0]?.doctor_opinion_status === "Opinion Rejected") {
        onOpenRejectModal();
        setMedicalOpinionRejectedButton(true);
      } else if (
        patientDetails[0]?.doctor_opinion_status === "Opinion Provided"
      ) {
        setShowMedicalOpinion(true);
      } else if (
        patientDetails[0]?.doctor_opinion_status === "Need Additional Info"
      ) {
        setReports(patientDetails[0]?.patient_reports);
        setIsReportsModalOpen(true);
      }
    } else if (patientDetails[0]?.ips_opinion_status === "Opinion Provided") {
      window.open(patientDetails[0]?.medical_op_report, "_blank");
    }
  };

  const handleBackToSummary = () => {
    setShowMedicalOpinion(false);
  };

  const getOpinionStatusText = (ipsStatus, doctorStatus) => {
    if (ipsStatus === "Not Requested") return "N/A";
    if (ipsStatus === "Received From Doctor") {
      return doctorStatus === "Opinion Rejected" ? "Rejected" : "View";
    }
    if (ipsStatus === "Pending From Doctor") return "Pending";
    if (ipsStatus === "Opinion Provided") return "View";
    return ipsStatus;
  };

  const handleRegeneratePDF = (regenerateData) => {
    const payload = {
      tr_id: trid,
      regenerate_font: true,
      source: "Web",
      generate_pdf: selectedMType,
      ...regenerateData,
    };
    dispatch(fetchGenerateTemplateRequest(payload));
  };

  const handleSendForApprovalClick = () => {
    if (selectedMType === "VIL") {
      const payload = {
        tr_id: trid,
        action: "send_for_approval",
      };
      dispatch(fetchVilUpdateRequest(payload));
    } else {
      const payload = {
        tr_id: trid,
        opinion_type: "confirm_opinion",
      };
      dispatch(fetchProvideOpinionRequest(payload));
    }
  };

  const handleGeneratedFilesClose = () => {
    setShowGeneratedFiles(false);
    setShowTemplateForm(false);
    setSelectedMType("");
    setInitialFormData(null);
    setIsGeneratingTemplate(false);
  };

  const handleVilRequest = () => {
    if (patientDetails[0]?.visa_sub_status === "Sent To HCF") {
      if (patientDetails[0]?.vil_report_1 !== "") {
        window.open(patientDetails[0].vil_report_1, "_blank");
        onCloseVilReportModal();
      }
    } else if (patientDetails[0]?.visa_sub_status === "Pending VIL") {
      setShowGeneratedFiles(true);
      setSelectedMType("VIL");
      const payload = {
        tr_id: trid,
        regenerate_font: false,
        source: "Web",
        generate_pdf: "VIL",
      };
      setIsGeneratingTemplate(true);
      dispatch(fetchGenerateTemplateRequest(payload));
      setShowTemplateForm(false);
    } else if (patientDetails[0]?.visa_sub_status === "Approval Pending") {
      console.log("Fetching template approval data for VIL...");

      if (patientDetails[0]?.vil_report !== "") {
        onOpenVilReportModal();
      }
    }
  };
  const handleVilRequest2 = () => {
    if (patientDetails[0]?.visa_sub_status === "Sent To HCF") {
      if (patientDetails[0]?.vil_report_2 !== "") {
        window.open(patientDetails[0].vil_report_2, "_blank");
        onCloseVilReportModal();
      }
    } else if (patientDetails[0]?.visa_sub_status === "Pending VIL") {
      // dispatch(fetchVilTemplateRequest({ tr_id: trid, source: "Web" }));
      // onOpenVilProcessModal();
      setShowGeneratedFiles(true);
      setSelectedMType("VIL");
      const payload = {
        tr_id: trid,
        regenerate_font: false,
        source: "Web",
        generate_pdf: "VIL",
      };
      setIsGeneratingTemplate(true);
      dispatch(fetchGenerateTemplateRequest(payload));
      setShowTemplateForm(false);
    } else if (patientDetails[0]?.visa_sub_status === "Approval Pending") {
      console.log("Fetching template approval data for VIL...");

      if (patientDetails[0]?.vil_report !== "") {
        onOpenVilReportModal();
      }
    }
  };
  const handleRequestDetails = () => {
    onOpenRequestDetailsModal();
  };

  const handleShareWithHCF = () => {
    if (patientDetails[0]?.vil_report !== "") {
      onOpenShareWithHCFModal();
    }
  };

  const handleReShareToHCF = (file) => {
    const payload = new FormData();
    payload.append("tr_id", trid);
    payload.append("action", "share_with_hcf");
    payload.append("file", file);
    dispatch(fetchVilUpdateRequest(payload));
    onCloseShareWithHCFModal();
  };

  const handleDownloadVilReport = () => {
    if (patientDetails[0]?.vil_report_2 !== "") {
      window.open(patientDetails[0].vil_report_2, "_blank");
      onCloseVilReportModal();
    }
  };

  const handleEditVilReport = () => {
    // Logic for editing the VIL report
    console.log("Edit VIL report");
    // Close the current modal
    onCloseVilReportModal();
    // Fetch templates and open the selection modal
    dispatch(fetchVilTemplateRequest({ tr_id: trid, source: "Web" }));
    onOpenVilProcessModal();
  };

  const handleApproveVilReport = () => {
    // Logic for approving the VIL report
    console.log("Approve VIL report");
    // Example implementation:
    const payload = {
      tr_id: trid,
      action: "approve",
    };
    dispatch(fetchVilUpdateRequest(payload));
    onCloseVilReportModal();
  };

  const spacing = useBreakpointValue({ base: 2, md: 4 });

  return (
    <Layout>
      {showTemplateForm ? (
        <TemplateForm
          shareOpinionFieldsData={templateDataFields}
          onBack={handleTemplateBack}
          onSubmit={handleTemplateSubmit}
          selectedMType={selectedMType}
          isVilTemplate={selectedMType === "VIL"}
        />
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mt="-20px">
            {!(
              (isFromPatientArrivals &&
                patientDetails[0]?.pickup_details_status === "Rejected") ||
              (isFromMedicalOpinion &&
                patientDetails[0]?.ips_opinion_status === "Opinion Rejected") ||
              (isFromVilRequests &&
                patientDetails[0]?.visa_sub_status === "Rejected")
            ) && (
              <IconButton
                icon={<FcCancel />}
                size="lg"
                aria-label="reject"
                fontSize="30px"
                isRound={true}
                variant="solid"
                onClick={onOpenRejectModal}
              />
            )}

            <IconButton
              color="primary.1"
              size="lg"
              aria-label="Go Back"
              fontSize="30px"
              isRound={true}
              variant="solid"
              icon={<IoArrowBackCircleOutline />}
              onClick={handleGoBack}
            />
          </Box>
          <Box p={2}>
            <Box
              bg="primary.1"
              color="white"
              fontWeight={"bold"}
              fontSize="xl"
              p="8px"
              borderTopRadius={"md"}
            >
              Patient Details
            </Box>
            <Box bg="white" borderRadius="md" p={4}>
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1px 1fr" }}
                gap={4}
                alignItems="start"
              >
                <Box>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">Name:</PrimaryFormLabel>
                    <Text>{patientDetails[0]?.patient_name || "N/A"}</Text>
                  </Flex>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">Hospital:</PrimaryFormLabel>
                    <Text>{patientDetails[0]?.hospital || "N/A"}</Text>
                  </Flex>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">TR-ID:</PrimaryFormLabel>
                    <Text>{patientDetails[0]?.id || "N/A"}</Text>
                  </Flex>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">
                      Passport No:
                    </PrimaryFormLabel>
                    <Text>{patientDetails[0]?.passport_no || "N/A"}</Text>
                  </Flex>
                </Box>

                <Divider
                  orientation="vertical"
                  borderColor="gray.300"
                  display={{ base: "none", md: "block" }}
                />

                <Box>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">Gender:</PrimaryFormLabel>
                    <Text>{patientDetails[0]?.gender || "N/A"}</Text>
                  </Flex>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">Age:</PrimaryFormLabel>
                    <Text>{patientDetails[0]?.age || "N/A"}</Text>
                  </Flex>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">
                      Mobile No.:
                    </PrimaryFormLabel>
                    <Text>{patientDetails[0]?.mobile_no || "N/A"}</Text>
                  </Flex>
                  <Flex mb={{ base: 1, md: 2 }}>
                    <PrimaryFormLabel minW="120px">Country:</PrimaryFormLabel>
                    <Text>
                      {patientDetails[0]?.country_of_residency || "N/A"}
                    </Text>
                  </Flex>
                </Box>
              </Grid>
            </Box>
          </Box>
          {!showMedicalOpinion ? (
            <>
              <Box p={2}>
                <Box
                  bg="green.300"
                  color="white"
                  fontWeight="bold"
                  fontSize="xl"
                  p="8px"
                  borderTopRadius="md"
                >
                  Summary
                </Box>
                <Box bg="white" borderRadius="md" p={6}>
                  <Grid
                    templateColumns="1fr auto"
                    gap={spacing}
                    alignItems="center"
                  >
                    <PrimarySideHeader>Health Concern</PrimarySideHeader>
                    <Text>
                      {patientDetails[0]?.health_concern_label || "N/A"}
                    </Text>
                    <PrimarySideHeader>Requested Doctor</PrimarySideHeader>
                    <Text>{patientDetails[0]?.requested_doctor || "N/A"}</Text>
                    <PrimarySideHeader>Recommended Doctor</PrimarySideHeader>
                    <Text>
                      {patientDetails[0]?.appointed_doctor || "N/A"}{" "}
                      {patientDetails[0]?.ips_opinion_status !==
                        "Not Requested" &&
                        patientDetails[0]?.ips_opinion_status !==
                          "Received From Doctor" &&
                        patientDetails[0]?.ips_opinion_status !==
                          "Opinion Provided" && (
                          <Text
                            as="span"
                            color="blue.500"
                            cursor="pointer"
                            onClick={handleDoctorsEditClick}
                          >
                            ✎
                          </Text>
                        )}
                    </Text>
                    {isFromVilRequests && (
                      <>
                        <PrimarySideHeader>Embassy</PrimarySideHeader>
                        <Text>
                          {patientDetails[0]?.patient_embassy || "N/A"}{" "}
                          {patientDetails[0]?.vil_status !== "VIL Provided" && (
                            <Text
                              as="span"
                              color="blue.500"
                              cursor="pointer"
                              onClick={handleEmbassyEditClick}
                            >
                              ✎
                            </Text>
                          )}
                        </Text>
                      </>
                    )}
                    <PrimarySideHeader>Medical Opinion</PrimarySideHeader>
                    <Flex align="center" gap={2}>
                      <PrimaryButton
                        width="250px"
                        borderRadius="50px"
                        onClick={handleMedicalOpinionView}
                        isDisabled={
                          patientDetails[0]?.ips_opinion_status ===
                            "Not Requested" ||
                          patientDetails[0]?.ips_opinion_status ===
                            "New Opinion Request" ||
                          patientDetails[0]?.ips_opinion_status ===
                            "Pending From Doctor"
                        }
                        bg={
                          patientDetails[0]?.ips_opinion_status ===
                            "Received From Doctor" &&
                          patientDetails[0]?.doctor_opinion_status ===
                            "Opinion Rejected"
                            ? "red.500"
                            : "primary.1"
                        }
                      >
                        {getOpinionStatusText(
                          patientDetails[0]?.ips_opinion_status,
                          patientDetails[0]?.doctor_opinion_status
                        )}
                      </PrimaryButton>
                      {patientDetails[0]?.ips_opinion_status ===
                        "Pending From Doctor" && (
                        <Box
                          color="red.500"
                          fontSize="24px"
                          cursor={
                            patientDetails[0]?.doctor_reminder_count >= 3
                              ? "not-allowed"
                              : "pointer"
                          }
                        >
                          <BsBellFill
                            onClick={() =>
                              patientDetails[0]?.doctor_reminder_count < 3 &&
                              handleRemindClick(patientDetails[0]?.id)
                            }
                          />
                        </Box>
                      )}
                    </Flex>
                    <PrimarySideHeader>Reports</PrimarySideHeader>
                    <PrimaryButton
                      width="250px"
                      borderRadius="50px"
                      onClick={handleReportsView}
                      disabled={!patientDetails[0]?.patient_reports.length > 0}
                    >
                      {patientDetails[0]?.patient_reports.length > 0
                        ? "View"
                        : "N/A"}
                    </PrimaryButton>
                    <PrimarySideHeader>
                      Patient Passport Details
                    </PrimarySideHeader>
                    <PrimaryButton
                      width="250px"
                      borderRadius="50px"
                      disabled={isPatientPassportEmpty}
                      onClick={() => handlePassportModal(true)}
                    >
                      {patientDetails[0]?.patient_passport ? "View" : "N/A"}
                    </PrimaryButton>
                    <PrimarySideHeader>
                      Attenders Passport Details
                    </PrimarySideHeader>
                    <PrimaryButton
                      width="250px"
                      borderRadius="50px"
                      disabled={isAttendantPassportsEmpty}
                      onClick={() => handlePassportModal(false)}
                    >
                      {isAttendantPassportsEmpty ? "N/A" : "View"}
                    </PrimaryButton>
                    <PrimarySideHeader>VIL Request</PrimarySideHeader>
                    <PrimaryButton
                      width="250px"
                      borderRadius="50px"
                      onClick={handleVilRequest}
                      isDisabled={
                        !(
                          patientDetails[0]?.visa_sub_status ===
                            "Pending VIL" ||
                          patientDetails[0]?.visa_sub_status ===
                            "Sent To HCF" ||
                          patientDetails[0]?.visa_sub_status ===
                            "Approval Pending"
                        )
                      }
                    >
                      {patientDetails[0]?.visa_sub_status === "Sent To HCF" ||
                      patientDetails[0]?.visa_sub_status === "Approval Pending"
                        ? "View"
                        : patientDetails[0]?.visa_sub_status || "N/A"}
                    </PrimaryButton>
                    <PrimarySideHeader>Flight Tickets</PrimarySideHeader>
                    <PrimaryButton
                      width="250px"
                      borderRadius="50px"
                      onClick={handleFlightTicketView}
                      disabled={
                        patientDetails[0]?.flight_ticket_status ===
                        "Not Uploaded"
                      }
                    >
                      {patientDetails[0]?.flight_ticket_status !==
                      "Not Uploaded"
                        ? "View"
                        : "N/A"}
                    </PrimaryButton>
                    {patientDetails[0]?.pickup_details_status !==
                      "Not Requested" && (
                      <>
                        <PrimarySideHeader>Airport Pickup</PrimarySideHeader>
                        <PrimaryButton
                          width="250px"
                          borderRadius="50px"
                          onClick={handleAirportPickupButtonClick}
                          disabled={
                            patientDetails[0]?.pickup_details_status !==
                            "Pickup Details Provided"
                          }
                        >
                          {patientDetails[0]?.pickup_details_status ===
                          "Pickup Details Provided"
                            ? "View"
                            : patientDetails[0]?.pickup_details_status}
                        </PrimaryButton>
                      </>
                    )}
                  </Grid>
                </Box>
              </Box>

              {(isFromPatientArrivals ||
                isFromMedicalOpinion ||
                isFromVilRequests) && (
                <IconGroup
                  icons={
                    ICON_CONFIGS[
                      isFromPatientArrivals
                        ? "patientArrivals"
                        : isFromVilRequests
                        ? "vilRequest"
                        : "medicalOpinion"
                    ]
                  }
                  handlers={{
                    handleAirportPickupIconClick,
                    onOpenShareOpinionModal,
                    onOpenShareWithDoctorModal,
                    handleReportsView,
                    handleVilRequest,
                    handleVilRequest2,
                    handleRequestDetails,
                    handleShareWithHCF,
                  }}
                  patientDetails={patientDetails}
                />
              )}
            </>
          ) : (
            <Box p={2}>
              <Flex justify="space-between" align="center">
                <Box
                  bg="green.300"
                  color="white"
                  fontWeight="bold"
                  fontSize="xl"
                  p="8px"
                  borderTopRadius="md"
                  flex="1"
                >
                  Medical Opinion
                </Box>
                <IconButton
                  color="primary.1"
                  size="lg"
                  aria-label="Back to Summary"
                  fontSize="30px"
                  isRound={true}
                  variant="solid"
                  icon={<IoArrowBackCircleOutline />}
                  onClick={handleBackToSummary}
                  ml={2}
                />
              </Flex>
              <MedicalOpinion
                trID={patientDetails[0]?.id}
                doctorProvidedOpinion={doctorProvidedOpinion}
                onShareOpinion={onOpenShareOpinionModal}
              />
            </Box>
          )}
        </>
      )}
      <VilTemplateSelectionView
        isOpen={isVilProcessModalOpen}
        onClose={onCloseVilProcessModal}
        vilTemplates={vilTemplates}
        onSelect={handleVilTemplateSelection}
      />

      <SelectionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        trID={patientDetails[0]?.id}
        hospital={patientDetails[0]?.hospital}
        healthConcern={patientDetails[0]?.health_concern}
        handleSubmit={handleModalSubmit}
        modalType={modalType}
      />

      <ReportsViewModal
        isOpen={isReportsModalOpen}
        onClose={() => setIsReportsModalOpen(false)}
        reports={reports}
        hcfRemarks={patientDetails[0]?.hcf_mo_remarks}
        handleReportsModalSubmit={handleReportsModalSubmit}
        downloadFiles={downloadFiles}
        doctorProvidedOpinion={doctorProvidedOpinion}
      />

      {isPatientPassport ? (
        <PassportModal
          isOpen={isPassportModalOpen}
          onClose={onClosePassportModal}
          trDetails={patientDetails[0]}
          isPatientPassport={isPatientPassport}
          patientPassport={patientDetails[0]?.patient_passport}
        />
      ) : (
        <PassportModal
          isOpen={isPassportModalOpen}
          onClose={onClosePassportModal}
          trDetails={patientDetails[0]}
          isPatientPassport={isPatientPassport}
          attendantDetails={patientDetails[0]?.attendant_details}
        />
      )}
      <FlightTicketDetailsModal
        isOpen={isFlightTicketModalOpen}
        onClose={onCloseFlightTicketModal}
        flightDetails={patientDetails[0]?.flight_ticket_details}
        patientName={patientDetails[0]?.patient_name}
        attendantDetails={patientDetails[0]?.attendant_details}
        downloadFiles={downloadFiles}
      />
      <RejectOpinion
        isOpen={isRejectModalOpen}
        onClose={onCloseRejectModal}
        rejectionOpinion={rejectionOpinion}
        setRejectionOpinion={setRejectionOpinion}
        doctorOpinionData={rejectedOpinionData}
        isFromMedicalOpinion={medicalOpinionRejectedButton}
        setMedicalOpinionRejectedButton={setMedicalOpinionRejectedButton}
        onSubmit={handleRejectPatientSubmit}
      />
      <AirportPickUpModal
        isOpen={isAirportPickupModalOpen}
        onClose={onCloseAirportPickupModal}
        patientDetails={patientDetails[0]}
        handleAirportPickUpSubmit={handleAirportPickUpSubmit}
        isViewOnlyMode={isViewOnlyMode}
      />
      <Alert
        isOpen={showAlert}
        title={alertTitle}
        onClose={() => {
          setShowAlert(false);
        }}
        body={alertMessage}
        type={alertType}
        callback={
          (alertType === "success" ? () => handleLoadPage() : undefined) ||
          (alertType === "error" ? () => handleLoadPage() : undefined)
        }
      />
      <ShareWithDoctorModal
        isOpen={isShareWithDoctorModalOpen}
        onClose={onCloseShareWithDoctorModal}
        onSubmit={handleShareWithDoctorSubmit}
      />
      <ShareOpinionModal
        isOpen={isShareOpinionModalOpen}
        onClose={onCloseShareOpinionModal}
        onSubmit={handleShareOpinionClick}
      />
      <GeneratedFileView
        isOpen={showGeneratedFiles}
        onClose={handleGeneratedFilesClose}
        files={generateTemplateData}
        onRegenerateClick={handleRegeneratePDF}
        isLoading={false}
        initialFormData={initialFormData}
        handleSendForApprovalClick={handleSendForApprovalClick}
      />
      <RequestDetails
        isOpen={isRequestDetailsModalOpen}
        onClose={onCloseRequestDetailsModal}
        patientDetails={patientDetails[0]}
      />
      <ShareWithHCF
        isOpen={isShareWithHCFModalOpen}
        onClose={onCloseShareWithHCFModal}
        vilReports={patientDetails[0]?.vil_report || []}
        onReShareToHCF={handleReShareToHCF}
      />
      <Modal
        isOpen={isVilReportModalOpen}
        onClose={onCloseVilReportModal}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent maxWidth="80%">
          <ModalHeader>VIL Report</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" h="100%">
              <Box flex="1" overflow="auto">
                <iframe
                  src={patientDetails[0]?.vil_report}
                  style={{ width: "100%", height: "100%", minHeight: "80vh" }}
                  title="VIL Report"
                />
              </Box>
              <Flex justify="flex-end" mt={4} gap={2}>
                <PrimaryButton onClick={handleDownloadVilReport}>
                  Download
                </PrimaryButton>
                <PrimaryButton onClick={handleEditVilReport}>
                  Edit
                </PrimaryButton>
                <PrimaryButton
                  onClick={handleApproveVilReport}
                  colorScheme="green"
                >
                  Approve
                </PrimaryButton>
                <PrimaryButton
                  onClick={onCloseVilReportModal}
                  colorScheme="red"
                >
                  Cancel
                </PrimaryButton>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Layout>
  );
};

export default PatientDetails;
